/**
 * Form validation, as pure functions.
 *
 * Same reasoning as deviceViewModel: this is where the bugs are and where
 * exhaustive testing is cheap. The components below it collect strings and
 * render results; they decide nothing.
 *
 * Client-side validation is a courtesy, never a control. The API validates
 * everything again — `maxCameras` in particular is a licensing decision the
 * server refuses to default — so anything here exists to save a round trip and
 * give a better message, not to enforce a rule.
 */

export interface ValidationResult {
  ok: boolean;
  /** Field name to message. Empty when ok. */
  errors: Record<string, string>;
}

const ok: ValidationResult = { ok: true, errors: {} };

function fail(errors: Record<string, string>): ValidationResult {
  return { ok: Object.keys(errors).length === 0, errors };
}

export interface SiteForm {
  name: string;
  address: string;
  timezone: string;
}

export function validateSite(form: SiteForm): ValidationResult {
  const errors: Record<string, string> = {};
  if (form.name.trim() === '') errors['name'] = 'A site name is required.';
  return Object.keys(errors).length === 0 ? ok : fail(errors);
}

export interface DeviceForm {
  siteId: string;
  name: string;
  maxCameras: string;
}

export function validateDevice(form: DeviceForm): ValidationResult {
  const errors: Record<string, string> = {};

  if (form.siteId === '') errors['siteId'] = 'Choose a site.';
  if (form.name.trim() === '') errors['name'] = 'A device name is required.';

  /*
   * Parsed strictly. Number('') is 0 and Number('4 cameras') is NaN, so a lax
   * check would send the server a licence count nobody chose — and the server
   * requires this precisely because capacity is a commercial decision.
   */
  const raw = form.maxCameras.trim();
  if (raw === '') {
    errors['maxCameras'] = 'A camera limit is required.';
  } else if (!/^\d+$/.test(raw)) {
    errors['maxCameras'] = 'Camera limit must be a whole number.';
  } else if (Number.parseInt(raw, 10) < 1) {
    errors['maxCameras'] = 'Camera limit must be at least 1.';
  }

  return Object.keys(errors).length === 0 ? ok : fail(errors);
}

export interface CameraForm {
  onvifHost: string;
  subProfileUri: string;
  mainProfileUri: string;
  /**
   * Optional as a pair.
   *
   * A camera with no authentication is rare and inadvisable but real, so blank
   * is allowed. What is not allowed is half of a credential — a username with no
   * password produces an RTSP URI that fails in a way that looks like a broken
   * camera rather than a missing password.
   */
  username?: string;
  password?: string;
}

export function validateCamera(form: CameraForm): ValidationResult {
  const errors: Record<string, string> = {};

  if (form.onvifHost.trim() === '') errors['onvifHost'] = 'A camera address is required.';

  const username = (form.username ?? '').trim();
  // Not trimmed: leading and trailing spaces are legal in a password, and
  // silently removing them produces an authentication failure nobody can explain.
  const password = form.password ?? '';

  if (username !== '' && password === '') {
    errors['password'] = 'A password is required when a username is given.';
  }
  if (username === '' && password !== '') {
    errors['username'] = 'A username is required when a password is given.';
  }

  const sub = form.subProfileUri.trim();
  if (sub === '') {
    // Required, because the substream is what is recorded continuously.
    errors['subProfileUri'] = 'A substream RTSP URI is required.';
  } else if (!/^rtsps?:\/\//i.test(sub)) {
    errors['subProfileUri'] = 'Must be an rtsp:// or rtsps:// URI.';
  }

  // Mainstream is event-only and legitimately absent on a single-profile camera,
  // so it is optional — but if given it must still be an RTSP URI.
  const main = form.mainProfileUri.trim();
  if (main !== '' && !/^rtsps?:\/\//i.test(main)) {
    errors['mainProfileUri'] = 'Must be an rtsp:// or rtsps:// URI.';
  }

  return Object.keys(errors).length === 0 ? ok : fail(errors);
}

/**
 * Whether a claim code is still usable.
 *
 * Shown alongside the code so an installer is not handed something that expired
 * while the tab sat open.
 */
export function claimCodeStatus(
  expiresAt: string | null | undefined,
  now: number,
): { expired: boolean; label: string } {
  if (!expiresAt) return { expired: false, label: '' };

  const remainingMs = new Date(expiresAt).getTime() - now;
  if (!Number.isFinite(remainingMs)) return { expired: false, label: '' };
  if (remainingMs <= 0) return { expired: true, label: 'This code has expired. Reissue it.' };

  const hours = Math.floor(remainingMs / 3_600_000);
  if (hours >= 1) return { expired: false, label: `Valid for ${hours} more hour${hours === 1 ? '' : 's'}` };

  const minutes = Math.max(1, Math.floor(remainingMs / 60_000));
  return { expired: false, label: `Valid for ${minutes} more minute${minutes === 1 ? '' : 's'}` };
}
